import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import axios, { refreshAuthTokens } from '@/config/axios';
import type { User } from '@hms/shared';
import { useHospitalContextStore } from './hospitalContext';
import { useOrganizationContextStore } from './organizationContext';
import { useDoctorProfileStore } from './doctorProfile';
import { resetCurrentNurse } from '@/composables/useCurrentNurse';
import { resetCurrentStaff } from '@/composables/useCurrentStaff';
import { resetCurrentPatient } from '@/composables/useCurrentPatient';
import { offlineCacheService } from '@/services/offlineCacheService';
import { apiErrorMessage } from '@/utils/apiErrorTranslator';

const LOGIN_FAILED_KEY = 'auth.loginFailed';

// The sign-in refusals the API names; anything else is the generic sign-in failure.
const LOGIN_ERROR_KEYS = { INVALID_CREDENTIALS: 'errors.api.invalidCredentials', INACTIVE_ACCOUNT: 'errors.api.inactiveAccount' } as const;

export const useAuthStore = defineStore(
  'auth',
  () => {
    const user = ref<User | null>(null);
    const token = ref<string | null>(null);
    const refreshToken = ref<string | null>(null);
    const mustChangePassword = ref(false);

    const isAuthenticated = computed(() => !!token.value);

    /**
     * Establishes the authenticated session from a login-style response body
     * (the shape returned by both POST /auth/login and POST /auth/2fa/verify):
     * maps the user, stores tokens, sets the axios default header, and bootstraps
     * the hospital + organization context. Shared by `login` and `completeSession`
     * so the 2FA verify path issues a session identical to a password login.
     */
    const establishSession = (data: {
      user: {
        id: string;
        email: string;
        firstName: string;
        lastName: string;
        role: string;
        organizationId?: string | null;
        hospitalIds?: string[];
      };
      accessToken: string;
      refreshToken: string;
      mustChangePassword?: boolean;
    }) => {
      const { user: userData, accessToken, refreshToken: refreshTokenData, mustChangePassword: mustChange } = data;

      // Map backend user to frontend User type
      user.value = {
        id: userData.id,
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        role: userData.role as User['role'],
        organizationId: userData.organizationId ?? null,
        hospitalIds: userData.hospitalIds ?? [],
        permissions: [], // Backend doesn't return permissions yet
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      token.value = accessToken;
      refreshToken.value = refreshTokenData;
      mustChangePassword.value = mustChange || false;

      // Set axios default header
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

      // Bootstrap the active-hospital context from the JWT claims so
      // subsequent requests carry X-Hospital-Id. The API refuses the hospital
      // names and the organization until a required password change is made,
      // so those wait for `completePasswordChange`.
      useHospitalContextStore().initFromUser(user.value, { loadHospitalNames: !mustChangePassword.value });
      // Hydrate the org context (currency lives there as of migration
      // 20260519000001). Fire-and-forget — formatter falls back to a plain
      // number until /organizations/me resolves.
      if (!mustChangePassword.value) {
        useOrganizationContextStore().initFromUser(user.value);
      }
    };

    const login = async (email: string, password: string) => {
      try {
        const response = await axios.post('/auth/login', {
          email,
          password,
        });

        // CORA-155b: when the account has 2FA enabled the backend returns a
        // tokenless challenge instead of a session. Surface it so the caller
        // can route to the 2FA challenge step; do NOT establish a session yet.
        if (response.data?.twoFactorRequired === true) {
          return {
            success: true as const,
            twoFactorRequired: true as const,
            challengeId: response.data.challengeId as string,
            method: response.data.method as string,
          };
        }

        establishSession(response.data);

        return { success: true as const, mustChangePassword: mustChangePassword.value };
      } catch (error: unknown) {
        return { success: false as const, error: apiErrorMessage(error, LOGIN_FAILED_KEY, LOGIN_ERROR_KEYS) };
      }
    };

    /**
     * CORA-155b: completes a 2FA login challenge. Establishes the session from
     * the verify response exactly like a password login. The challenge code is
     * never persisted — only the resulting tokens are.
     */
    const completeSession = (data: Parameters<typeof establishSession>[0]) => {
      establishSession(data);
      return { mustChangePassword: mustChangePassword.value };
    };

    const logout = async () => {
      // F-07: tell the server to revoke the refresh token before dropping it.
      // Awaited so the revocation is actually in flight with a valid token, but
      // never allowed to fail the sign-out — a user who cannot reach the API
      // must still be able to log out of this device.
      const tokenToRevoke = refreshToken.value;
      if (tokenToRevoke) {
        try {
          await axios.post('/auth/logout', { refreshToken: tokenToRevoke });
        } catch {
          // Offline, expired, or already revoked — local teardown proceeds.
        }
      }

      user.value = null;
      token.value = null;
      refreshToken.value = null;
      delete axios.defaults.headers.common['Authorization'];
      useHospitalContextStore().reset();
      useOrganizationContextStore().reset();
      // Drop cached professional profiles so the next user doesn't inherit
      // the previous user's nurse / staff record.
      resetCurrentNurse();
      resetCurrentStaff();
      resetCurrentPatient();
      useDoctorProfileStore().reset();
      // CORA-164: wipe the offline patient-records snapshot so a shared device
      // does not leak the previous patient's cached records. Fire-and-forget —
      // the IndexedDB clear must not block the synchronous logout teardown.
      void offlineCacheService.clear();
    };

    const checkAuth = async () => {
      if (!token.value) return false;

      // Set axios default header if token exists
      axios.defaults.headers.common['Authorization'] = `Bearer ${token.value}`;

      // Rehydration path: pinia-plugin-persistedstate restored `user` + `token`
      // from localStorage, but the hospitalContext store's availableHospitals
      // may still carry placeholder names from a prior session (or be empty).
      // Refresh from /hospitals/mine so the top-bar shows real hospital names.
      if (user.value && !mustChangePassword.value) {
        void useHospitalContextStore().loadAvailableHospitals();
        // Refresh the org (currency may have been changed by an admin since
        // the session was persisted). initFromUser handles the super-admin /
        // patient short-circuits.
        useOrganizationContextStore().initFromUser(user.value);
      }

      // Token exists, consider user authenticated
      // Note: In production, you might want to validate the token with the backend
      return true;
    };

    /** Ends a required password change: the session may now load what the API refused until then. */
    const completePasswordChange = () => {
      mustChangePassword.value = false;
      useHospitalContextStore().initFromUser(user.value);
      useOrganizationContextStore().initFromUser(user.value);
    };

    /**
     * Applies tokens issued by a refresh. Called by the axios interceptor, which
     * owns the single-flight refresh — keeping this store in step matters because
     * `logout` revokes whatever refresh token it holds.
     */
    const applyRefreshedTokens = (accessToken: string, newRefreshToken: string) => {
      token.value = accessToken;
      refreshToken.value = newRefreshToken;
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    };

    const refreshAccessToken = async () => {
      if (!refreshToken.value) return false;

      try {
        // Delegates so this shares the interceptor's single in-flight refresh;
        // a second implementation would race it and rotation would revoke one
        // of the two tokens. `applyRefreshedTokens` lands the result here.
        const accessToken = await refreshAuthTokens();
        if (!accessToken) {
          await logout();
          return false;
        }
        return true;
      } catch {
        await logout();
        return false;
      }
    };

    return {
      user,
      token,
      // Must be returned, not merely declared: in a setup store only returned
      // refs become $state, and `persist.paths` can only pick from $state. Left
      // out, the refresh token vanished on reload — so silent refresh never ran
      // and logout had nothing to revoke (F-07).
      refreshToken,
      mustChangePassword,
      isAuthenticated,
      login,
      completeSession,
      logout,
      checkAuth,
      completePasswordChange,
      applyRefreshedTokens,
      refreshAccessToken,
    };
  },
  {
    persist: {
      paths: ['user', 'token', 'refreshToken', 'mustChangePassword'],
    },
  },
);
